import { UserService } from 'src/app/users/shared/user.service';
import { Component, ViewChild, OnInit, Input } from '@angular/core';
import { ProfileService } from '../shared/profile.service';
import { ProfileDupCheckerComponent } from '../profile-dup-checker/profile-dup-checker.component';
import { ProfileMin } from '../shared/profile-min.model';
import { NavigationService } from '../../shared/navigation/navigation.service';
import { ProfileRead } from '../shared/profile-read.model';
import { Subject } from 'rxjs';
import { ProfileEditRelationshipComponent } from '../profile-edit/profile-edit-relationship/profile-edit-relationship.component';
import { PagedResult } from '../../shared/model/generic-paged-result.model';
import { ProfileEditComponent } from '../profile-edit';
import { memberProspectEnum } from './member-prospect.enum';
import { ProfileAddAsMemberComponent } from '../profile-view/profile-add-as-member/profile-add-as-member.component';
import { ObservationService } from 'src/app/shared/observation/observation.service';
import { Dialog } from 'primeng/dialog';
import { UtilService } from 'src/app/shared';

@Component({
  selector: 'wl-profile-create',
  templateUrl: 'profile-new.component.html',
  styleUrls: ['profile-new.component.less'],
  standalone: false,
})
export class ProfileNewComponent implements OnInit {
  @ViewChild(Dialog) _dialog: Dialog;

  public profile: ProfileRead = new ProfileRead();
  public errorMessage = '';
  public saving = false;
  public newProfileId = 0;
  public editMode = false;
  public addAsMemberMode = false;
  public memberProspect: memberProspectEnum;
  public memberProspectOptions: any = memberProspectEnum;
  public showDialog = false;
  public disableAddMember = false;
  public disableAddProspect = false;
  public displayMemberActivityModal = false;
  public get hideDupeChecker(): boolean {
    return this.editMode && this.addAsMemberMode;
  }

  public get hideProfileForm(): boolean {
    return this.profileDupChecker.showResults || this.editMode || this.displayMemberActivityModal;
  }

  public get hideProfileRelationForm(): boolean {
    return !this.parentProfileId;
  }

  public get hideAddAsMemberForm(): boolean {
    return !this.addAsMemberMode;
  }

  public get hideEditForm(): boolean {
    return !this.editMode;
  }

  public get hideSaveAndIgnoreButton(): boolean {
    return this.editMode || this.addAsMemberMode || !this.profileDupChecker.profileResults.TotalCount;
  }

  @Input('parentProfileId') parentProfileId: number;
  @Input('suspendNavigateAway') suspendNavigateAway = false;

  public parentProfileIdSubject: Subject<number> = new Subject<number>();

  @ViewChild(ProfileDupCheckerComponent, { static: true }) public profileDupChecker: ProfileDupCheckerComponent;
  @ViewChild(ProfileEditRelationshipComponent) public proifleRelationEdit: ProfileEditRelationshipComponent;
  @ViewChild(ProfileEditComponent) public profileEditComponent: ProfileEditComponent;
  @ViewChild(ProfileAddAsMemberComponent) public profileAddAsMember: ProfileAddAsMemberComponent;

  constructor(
    private _navigationService: NavigationService,
    private _profileService: ProfileService,
    private _observationService: ObservationService,
    private _userService: UserService,
    private _utilService: UtilService
  ) {}

  ngOnInit() {
    this.reset();

    this._userService.getCurrentUserSecurityElementAccessLevel('Set_Profile_As_Member').then((accessLevel: number) => {
      this.disableAddMember = accessLevel <= 1;
    });

    this._userService.getCurrentUserSecurityElementAccessLevel('Set_Profile_As_Prospect').then((accessLevel: number) => {
      this.disableAddProspect = accessLevel <= 1;
    });
  }

  /**
   * Reset the component to a starting state. Useful when opening.
   */
  public reset() {
    this.profile = new ProfileRead();
    if (!this.parentProfileId) {
      this.profile.OrgInd = true;
    } else {
      this.profile.OrgInd = false;
    }
    this.errorMessage = '';
    this.profileDupChecker.hide();
    this.profileDupChecker.profileResults = new PagedResult();
    this.newProfileId = 0;
    this.editMode = false;
    this.addAsMemberMode = false;
    this.memberProspect = memberProspectEnum.NonMember;
    this.displayMemberActivityModal = false;
  }

  public open() {
    this.reset();
    if (this.parentProfileId) {
      this.parentProfileIdSubject.next(this.parentProfileId);
      this._profileService.getProfileMin(this.parentProfileId).then((data) => {
        this.proifleRelationEdit.profileRelation.ProfileID = data.ProfileId;
        this.proifleRelationEdit.profileRelation.ParentReportName = data.ReportName;
      });
    }
    this.showDialog = true;
  }

  public newCancel() {
    this.showDialog = false;
  }

  // #region save click events
  // #endregion
  public saveAndIgnoreClick() {
    this.saving = true;
    this.createProfile(false);
  }

  /**
   * Save profile click
   * @param overrideDupeCheck don't worry about a dupe check. just run the create anyway
   */
  public saveClick() {
    const profileName: string = this.profile.OrgInd ? this.profile.OrgName : `${this.profile.FirstName} ${this.profile.LastName}`;
    this.saving = true;

    // check for dupes
    this.profileDupChecker.getPossibleDuplicates(profileName).then((data) => {
      if (!data.TotalCount) {
        this.createProfile();
      } else {
        //._dialog.center();
        this.saving = false;
      }
      this.displayMemberActivityModal = this.addAsMemberMode;
    });
  }

  public saveClickAndClose() {
    this.saving = true;
    this.createProfile(false, true);
  }

  public saveProfileEditClick() {
    this.profileEditComponent.saveClick(false);
    this._observationService.profileUpdated(this.profileEditComponent.profileId);
    if (this.parentProfileId) {
      this._observationService.relationAdded(this.parentProfileId);
    }
    this.showDialog = false;
  }

  public saveAddAsMemberClick() {
    if (this.profileAddAsMember.canSave) {
      this.saving = true;
      this._profileService.getProfile(this.newProfileId).then((data: ProfileRead) => {
        this.profile = data;
        if (this.profileAddAsMember) {
          this.profileAddAsMember.profile = data;
          this.profileAddAsMember.save().then(
            () => {
              this.showDialog = false;
              this.saving = false;
              this._navigationService.navigateToProfileEdit(this.newProfileId);
            },
            () => {
              this.saving = false;
            }
          );
        }
      });
    }
  }

  /**
   * Attempt to create a profile
   */
  public createProfile(continueEditing: boolean = false, bypassContinueEditing?: boolean) {
    this.saving = true;

    this.profile.Member = this.memberProspect === memberProspectEnum.Member;
    this.profile.Prospect = this.memberProspect === memberProspectEnum.Prospect;
    const switchToAddAsMember = this.profile.Member;
    this.addAsMemberMode = this.profile.Member;
    this.displayMemberActivityModal = this.profile.Member && continueEditing;

    continueEditing =
      continueEditing || this.memberProspect === memberProspectEnum.Prospect || this.parentProfileId > 0 || this.profile.Member;

    if (bypassContinueEditing) {
      continueEditing = false;
    }
    const promises = [];

    if (this.parentProfileId) {
      this.profile.Title = this.proifleRelationEdit.profileRelation.Title;
    }

    promises.push(
      this._profileService.createProfile(this.profile).then((newId) => {
        this.saving = false;
        if (newId > 0) {
          if (this.parentProfileId) {
            this.proifleRelationEdit.profileRelation.RelProfileID = newId;
            return this.proifleRelationEdit.save().then(() => {
              return Promise.resolve(newId);
            });
          }
          return Promise.resolve(newId);
        }
      })
    );

    Promise.all(promises)
      .then(([newId]) => {
        this.profile.ProfileId = newId;
        this.newProfileId = newId;
        if (this.parentProfileId) {
          this._observationService.relationAdded(this.parentProfileId);
        }
        if (!this.suspendNavigateAway && !this.addAsMemberMode) {
          if (continueEditing) {
            if (!switchToAddAsMember) {
              this._navigationService.navigateToProfileEdit(newId);
              this.showDialog = false;
            }
          } else {
            this._navigationService.navigateToProfileView(newId, true);
            this.showDialog = false;
          }
        } else if (continueEditing) {
          this.editMode = !switchToAddAsMember;
          this.newProfileId = newId;
          if (this.profileAddAsMember) {
            this.profileAddAsMember.profile = this.profile;
          }
          this._profileService.getProfileRelation(this.parentProfileId, this.newProfileId).then((relation) => {
            this.profileEditComponent.profileRelationId = relation.RelationID;
            this.profileEditComponent.setupComponentData();
          });
        } else {
          if (this.profile.Member) {
            this.profileAddAsMember.profile = this.profile;
            this.profileAddAsMember.save();
          }
          this.showDialog = false;
        }
        if (continueEditing) {
          this.addAsMemberMode = switchToAddAsMember;
        }
      })
      .catch((error: any) => {
        this.saving = false;
        this.errorMessage = this._utilService.extractErrorMessage(error);
      });
  }

  /**
   * Run when any name or OrgInd changes. Checks for dupes and tells the user about it.
   */
  public onNameChanged() {
    const profileName: string = this.profile.OrgInd ? this.profile.OrgName : `${this.profile.FirstName} ${this.profile.LastName}`;
    // skip empty string
    if (profileName) {
      this.profileDupChecker.getPossibleDuplicates(profileName);
    } else {
      this.profileDupChecker.hide();
    }
  }

  public onOrgNameChanged() {
    this.profile.ASF = this.profile.OrgName.trim();
    this.onNameChanged();
  }

  /**
   * Toggle the visibility of the dupe checker
   */
  public toggleDupCheckVisible() {
    if (this.profileDupChecker.showResults) {
      this.profileDupChecker.hide();
    } else {
      this.profileDupChecker.show();
    }
  }

  /**
   * called when the user selects a profile in the dupe checker. redirects to view that profile.
   * @param profile the profile that's been selected
   */
  public profileSelected(profile: ProfileMin) {
    if (profile && profile.ProfileId) {
      if (!this.suspendNavigateAway) {
        this._navigationService.navigateToProfileView(profile.ProfileId, true);
        this.showDialog = false;
      }
      this.newProfileId = profile.ProfileId;
      if (this.parentProfileId) {
        this.profileEditComponent.profileId = this.newProfileId;
        this.profileEditComponent.setupComponentData();
      }
      this.editMode = true;
    }
  }
}
